import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Button } from "../ui/Button";

const navLinks = [
  { label: "About", href: "#about" },
  { label: "Solutions", href: "#solutions" },
  { label: "Pricing", href: "#pricing" },
  { label: "Team", href: "#team" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  // Handle background blur on scroll
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  /**
   * Professional Smooth Scroll Handler
   * This solves the mobile "non-responsive" issue by manually scrolling 
   * and providing a small offset so the navbar doesn't cover section titles.
   */
  const handleScrollTo = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    setMobileOpen(false); // Close menu immediately

    const targetId = href.replace("#", "");
    const element = document.getElementById(targetId);

    if (element) {
      // Small timeout allows the mobile menu exit animation to start
      setTimeout(() => {
        const offset = 80; // This matches your navbar height
        const bodyRect = document.body.getBoundingClientRect().top;
        const elementRect = element.getBoundingClientRect().top;
        const elementPosition = elementRect - bodyRect;
        const offsetPosition = elementPosition - offset;

        window.scrollTo({
          top: offsetPosition,
          behavior: "smooth",
        });
      }, 150);
    }
  };

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white/90 backdrop-blur-md shadow-sm" : "bg-transparent"
      }`}
    >
      <div className="max-w-[95%] mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo Section */}
          <div className="flex items-center gap-3">
            <img
              src="https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/user_691713e6ad5c4c8f0a9a730b/f8dfa1ffd_SAVE_20250228_130825.jpg"
              alt="CipherGenix"
              className="w-8 h-8 rounded-full object-cover shadow-sm"
            />
            <span className="font-medium text-lg text-slate-900 tracking-tight">
              CipherGenix
            </span>
          </div>

          {/* Desktop Navigation Links */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                onClick={(e) => handleScrollTo(e, link.href)}
                className="text-sm font-medium transition-colors text-slate-600 hover:text-slate-900"
              >
                {link.label}
              </a>
            ))}
          </div>

          {/* Desktop Call to Action */}
          <div className="hidden md:block">
            <a href="#contact" onClick={(e) => handleScrollTo(e, "#contact")}>
              <Button className="bg-[#1e3a5f] hover:bg-[#2dd4bf] text-white rounded-full px-6 transition-all duration-300 shadow-md active:scale-95">
                Contact Us
              </Button>
            </a>
          </div>

          {/* Mobile Menu Toggle Button */}
          <button
            className="md:hidden p-2 rounded-lg hover:bg-slate-100 transition-colors"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle Menu"
          >
            {mobileOpen ? (
              <X className="w-6 h-6 text-slate-900" />
            ) : (
              <Menu className="w-6 h-6 text-slate-900" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-white border-t border-slate-100 shadow-xl overflow-hidden"
          >
            <div className="max-w-7xl mx-auto px-6 py-8 space-y-6 flex flex-col">
              {navLinks.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  className="text-lg font-medium text-slate-600 hover:text-[#1e3a5f] py-2 border-b border-slate-50 transition-colors"
                  onClick={(e) => handleScrollTo(e, link.href)}
                >
                  {link.label}
                </a>
              ))}
              
              <div className="pt-4">
                <a 
                  href="#contact" 
                  onClick={(e) => handleScrollTo(e, "#contact")}
                >
                  <Button className="w-full h-14 bg-[#1e3a5f] hover:bg-[#2dd4bf] text-white rounded-xl text-lg font-semibold transition-all active:scale-[0.98]">
                    Contact Us
                  </Button>
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
}